import React from 'react';
import { Link } from 'react-router-dom';
import { Bar, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, BarElement, PointElement, LineElement,
  Filler, Tooltip, Legend,
} from 'chart.js';
import ChartCard from '../components/chart/ChartCard';
import { useDashboardData } from '../data/DashboardDataProvider';
import { setOptions, chartConfig, chartConfigLineMultiAxis } from '../utils/chartConfigs';
import { colors } from '../utils/colors';
import {
  getDataSet, getSumDataSet, getFilteredData, setDatesAsWeeks, setDatesAsMonths,
  groupBy, getDataSetGrouped, getDataSetGroupedAvg,
} from '../utils/dataManager';

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Filler, Tooltip, Legend);

const STACKED = true;
const FILL = true;
const MULTIAXIS = true;

const daysAgo = 180;
const now = new Date();
const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
const startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - daysAgo);
startDate.setDate(startDate.getDate() - startDate.getDay() + 1);
const daysSinceStartDate = Math.round((today - startDate) / (1000 * 60 * 60 * 24));

const DashboardPage = () => {
  const { analyticsData, errors } = useDashboardData();
  const { subscriptionsTrialvsPaid, flagsPerDay, songsPerDay, analyticsEventsPerDay, usersPerDay, subscribersPerDay, usersPerMonth } = analyticsData;

  const flagsFiltered = getFilteredData(flagsPerDay, daysSinceStartDate);
  const flagsGroupedByWeek = groupBy(setDatesAsWeeks(flagsFiltered), 'creation_date');
  const flagsLabels = Object.keys(flagsGroupedByWeek);

  const songsFiltered = getFilteredData(songsPerDay, daysSinceStartDate);
  const songsGroupedByWeek = groupBy(setDatesAsWeeks(songsFiltered), 'creation_date');
  const songsLabels = Object.keys(songsGroupedByWeek);

  const analyticsEventsFiltered = getFilteredData(analyticsEventsPerDay, daysSinceStartDate);
  const analyticsEventsGroupedByWeek = groupBy(setDatesAsWeeks(analyticsEventsFiltered), 'creation_date');
  const analyticsEventsLabels = Object.keys(analyticsEventsGroupedByWeek);

  const usersFiltered = getFilteredData(usersPerDay, daysSinceStartDate);
  const usersGroupedByWeek = groupBy(setDatesAsWeeks(usersFiltered), 'creation_date');
  const usersLabels = Object.keys(usersGroupedByWeek);

  const subscribersFiltered = getFilteredData(subscribersPerDay, daysSinceStartDate);
  const subscribersGroupedByWeek = groupBy(setDatesAsWeeks(subscribersFiltered), 'creation_date');
  const subscribersTimeLabels = Object.keys(subscribersGroupedByWeek);

  const subscribersPaidPlus = getSumDataSet([
    getDataSetGroupedAvg(subscribersGroupedByWeek, 'subscribers_paid_plus_monthly'),
    getDataSetGroupedAvg(subscribersGroupedByWeek, 'subscribers_paid_plus_yearly'),
  ]);
  const subscribersPaidSongwriter = getSumDataSet([
    getDataSetGroupedAvg(subscribersGroupedByWeek, 'subscribers_paid_songwriter_monthly'),
    getDataSetGroupedAvg(subscribersGroupedByWeek, 'subscribers_paid_songwriter_yearly'),
  ]);
  const subscribersPaidPro = getSumDataSet([
    getDataSetGroupedAvg(subscribersGroupedByWeek, 'subscribers_paid_pro_monthly'),
    getDataSetGroupedAvg(subscribersGroupedByWeek, 'subscribers_paid_pro_yearly'),
  ]);

  const activeUsersStudio = getDataSetGroupedAvg(subscribersGroupedByWeek, 'active_users_studio');
  const activeUsersSongwriter = getDataSetGroupedAvg(subscribersGroupedByWeek, 'active_users_songwriter');
  const activeUsersExpress = getDataSetGroupedAvg(subscribersGroupedByWeek, 'active_users_express');

  const newSongwriterSongs = getDataSetGrouped(songsGroupedByWeek, 'appname_songwriter');
  const newStudioSongs = getDataSetGrouped(songsGroupedByWeek, 'appname_studio');
  const newExpressSongs = getDataSetGrouped(songsGroupedByWeek, 'source_type_recording');
  const newStudioAndSongwriterSongs = getDataSetGrouped(songsGroupedByWeek, 'source_type_manual');

  const paidSubscriptionSongwriter = getDataSetGrouped(flagsGroupedByWeek, 'subscription_paid_songwriter');
  const trialSubscriptionSongwriter = getDataSetGrouped(flagsGroupedByWeek, 'subscription_trial_songwriter');
  const paidSubscriptionPlus = getDataSetGrouped(flagsGroupedByWeek, 'subscription_paid_plus');
  const trialSubscriptionPlus = getDataSetGrouped(flagsGroupedByWeek, 'subscription_trial_plus');
  const paidSubscriptionPro = getDataSetGrouped(flagsGroupedByWeek, 'subscription_paid_pro');
  const trialSubscriptionPro = getDataSetGrouped(flagsGroupedByWeek, 'subscription_trial_pro');

  const loginSongwriter = getDataSetGrouped(flagsGroupedByWeek, 'login_songwriter');
  const loginSongwriterWin = getDataSetGrouped(flagsGroupedByWeek, 'login_songwriter_win');
  const loginSongwriterOsx = getDataSetGrouped(flagsGroupedByWeek, 'login_songwriter_osx');
  const loginSongwriterOsxAppstore = getDataSetGrouped(flagsGroupedByWeek, 'login_songwriter_osx_appstore');
  const loginStudio = getDataSetGrouped(flagsGroupedByWeek, 'login_studio');
  const loginStudiorWin = getDataSetGrouped(flagsGroupedByWeek, 'login_studio_win');
  const loginStudioOsx = getDataSetGrouped(flagsGroupedByWeek, 'login_studio_osx');
  const loginStudioOsxAppstore = getDataSetGrouped(flagsGroupedByWeek, 'login_studio_osx_appstore');
  const loginExpress = getDataSetGrouped(flagsGroupedByWeek, 'login_express');
  const loginExpressIpad = getDataSetGrouped(flagsGroupedByWeek, 'login_express_ipad');
  const loginExpressIphone = getDataSetGrouped(flagsGroupedByWeek, 'login_express_iphone');
  const loginMyScorecloud = getDataSetGrouped(flagsGroupedByWeek, 'login_my_scorecloud');

  const programStartSongwriter = getDataSetGrouped(analyticsEventsGroupedByWeek, 'program_start');
  const recordingStart = getDataSetGrouped(analyticsEventsGroupedByWeek, 'recording_start');
  const audioImport = getDataSetGrouped(analyticsEventsGroupedByWeek, 'audio_import');
  const videoImport = getDataSetGrouped(analyticsEventsGroupedByWeek, 'video_import');
  const songDisplay = getDataSetGrouped(analyticsEventsGroupedByWeek, 'song_display');
  const recordingPlusImport = getSumDataSet([recordingStart, audioImport, videoImport]);
  const songDisplayYouTube = getDataSetGrouped(analyticsEventsGroupedByWeek, 'youtube_song_display');

  const fiveDaysAgoData = getFilteredData(analyticsEventsPerDay, 5);
  const songDisplayYouTubePerDay = getDataSet(fiveDaysAgoData, 'youtube_song_display');

  const subscriptionChargedSuccessfully = getDataSetGrouped(analyticsEventsGroupedByWeek, 'subscription_charged_successfully');
  const subscriptionChargedSuccessfullySum = getDataSetGrouped(analyticsEventsGroupedByWeek, 'subscription_charged_successfully_sum');
  const subscriptionChargedSuccessfullyPlusSum = getDataSetGrouped(analyticsEventsGroupedByWeek, 'subscription_charged_successfully_plus_sum');
  const subscriptionChargedSuccessfullyProSum = getDataSetGrouped(analyticsEventsGroupedByWeek, 'subscription_charged_successfully_pro_sum');
  const subscriptionChargedSuccessfullySongwriterSum = getDataSetGrouped(analyticsEventsGroupedByWeek, 'subscription_charged_successfully_songwriter_sum');

  const webDownloadSongwriterMac = getDataSetGrouped(analyticsEventsGroupedByWeek, 'web_download_songwriter_mac');
  const webDownloadSongwriterWin = getDataSetGrouped(analyticsEventsGroupedByWeek, 'web_download_songwriter_windows');
  const webDownloadStudioMac = getDataSetGrouped(analyticsEventsGroupedByWeek, 'web_download_studio_mac');
  const webDownloadStudioWin = getDataSetGrouped(analyticsEventsGroupedByWeek, 'web_download_studio_windows');

  const usersMyScorecloud = getDataSetGrouped(usersGroupedByWeek, 'registration_app_myscorecloud');
  const usersStudio = getDataSetGrouped(usersGroupedByWeek, 'registration_app_studio');
  const usersExpress = getDataSetGrouped(usersGroupedByWeek, 'registration_app_express');
  const usersUnknown = getDataSetGrouped(usersGroupedByWeek, 'registration_app_unknown');
  const usersSongwriter = getDataSetGrouped(usersGroupedByWeek, 'registration_app_songwriter');

  const usersPerMonthTimeLabels = setDatesAsMonths(usersPerMonth.data).map(d => d.creation_date);
  const usersPerMonthSongwriterValues = [
    usersPerMonth.data.map(d => parseInt(d.songwriter_start_song_users) || 0),
    usersPerMonth.data.map(d => Math.max(parseInt(d.songwriter_finish_song_users || 0), parseInt(d.cloud_save_users || 0))),
  ];
  const usersPerMonthStudioValues = [
    usersPerMonth.data.map(d => parseInt(d.studio_start_song_users) || 0),
    usersPerMonth.data.map(d => parseInt(d.studio_finish_song_users) || 0),
  ];

  const expectedExpressRevenue = [];
  const expectedExpressRevenueIpad = [];
  const expectedExpressRevenueIphone = [];
  for (let i = 0; i < loginExpressIpad.length; i++) {
    expectedExpressRevenue[i] = Math.round(loginExpress[i] * 2.99);
    expectedExpressRevenueIpad[i] = Math.round(loginExpressIpad[i] * 2.99);
    expectedExpressRevenueIphone[i] = Math.round(loginExpressIphone[i] * 2.99);
  }

  const subscriptionsTrialVsPaid = [subscriptionsTrialvsPaid.dataTrial, subscriptionsTrialvsPaid.dataPaid];

  return (
    <section>
      {songDisplayYouTubePerDay.some(v => v < 5) && (
        <div className="alert alert-error">
          Warning! At least one of the last 5 days had less than 5 successful YouTube-imports.{' '}
          <Link to="/data/daily#youtube-finished">Check</Link> if something is wrong!
        </div>
      )}

      <div className="chart-grid">
        <div className="chart-col">
          <ChartCard header="Current Subscription Count" loading={subscriptionsTrialvsPaid.loading} error={errors.subscriptionsTrialvsPaid}
            values={subscriptionsTrialVsPaid} description="Number of active subscriptions right now" hideAvg>
            <Bar data={chartConfig(subscriptionsTrialvsPaid.subscriptionNames, ['Trial', 'Paid'], subscriptionsTrialVsPaid, [colors.primary, colors.secondary])} />
          </ChartCard>
        </div>

        <div className="chart-col">
          <ChartCard header="Subscribers" loading={subscribersPerDay.loading} error={errors.subscribersPerDay}
            values={[subscribersPaidPlus, subscribersPaidSongwriter, subscribersPaidPro]} description="Snapshot of active subscriptions over time" hideTotal>
            <Line data={chartConfig(subscribersTimeLabels, ['Paid Plus', 'Paid Songwriter', 'Paid Pro'],
              [subscribersPaidPlus, subscribersPaidSongwriter, subscribersPaidPro], [colors.teal, colors.yellow, colors.purple])} options={setOptions()} />
          </ChartCard>
        </div>

        <div className="chart-col">
          <ChartCard header="Monthly Active Users - ScoreCloud Songwriter" loading={usersPerMonth.loading} error={errors.usersPerMonth}
            values={usersPerMonthSongwriterValues} description="Unique users per month with important action in the ScoreCloud Songwriter client." hideTotal hideAvg>
            <Line data={chartConfig(usersPerMonthTimeLabels, ['Users with Song Start', 'Users with Song Finish'],
              usersPerMonthSongwriterValues, [colors.teal, colors.yellow])} options={setOptions()} />
          </ChartCard>
        </div>

        <div className="chart-col">
          <ChartCard header="Monthly Active Users - ScoreCloud Studio" loading={usersPerMonth.loading} error={errors.usersPerMonth}
            values={usersPerMonthStudioValues} description="Unique users per month with important action in the ScoreCloud Studio client." hideTotal hideAvg>
            <Line data={chartConfig(usersPerMonthTimeLabels, ['Users with Song Start', 'Users with Song Finish'],
              usersPerMonthStudioValues, [colors.teal, colors.yellow])} options={setOptions()} />
          </ChartCard>
        </div>

        <div className="chart-col">
          <ChartCard header="30 Day Action Users (Stacked)" loading={subscribersPerDay.loading} error={errors.subscribersPerDay}
            values={[activeUsersStudio, activeUsersSongwriter, activeUsersExpress]} hideTotal description="Number of users with any action within 30 days">
            <Line data={chartConfig(subscribersTimeLabels, ['Studio', 'Songwriter', 'Express'],
              [activeUsersStudio, activeUsersSongwriter, activeUsersExpress],
              [colors.variant_lightblue, colors.variant_lightyellow, colors.variant_lightred], FILL, STACKED)} options={setOptions(STACKED)} />
          </ChartCard>
        </div>

        <div className="chart-col">
          <ChartCard header="User Registration per Client (Stacked)" loading={usersPerDay.loading} error={errors.usersPerDay}
            values={[usersMyScorecloud, usersStudio, usersExpress, usersSongwriter, usersUnknown]} description="Number of new registered users per registration client">
            <Line data={chartConfig(usersLabels, ['My Scorecloud', 'Studio', 'Express', 'Songwriter', 'Unknown'],
              [usersMyScorecloud, usersStudio, usersExpress, usersSongwriter, usersUnknown],
              [colors.variant_lightgreen, colors.variant_lightblue, colors.variant_lightred, colors.variant_lightyellow, colors.variant_lightpurple], FILL, STACKED)} options={setOptions(STACKED)} />
          </ChartCard>
        </div>

        <div className="chart-col">
          <ChartCard header="New Subscriptions (Stacked)" loading={flagsPerDay.loading} error={errors.flagsPerDay}
            values={[trialSubscriptionPlus, paidSubscriptionPlus, trialSubscriptionSongwriter, paidSubscriptionSongwriter, trialSubscriptionPro, paidSubscriptionPro]}
            description="Number of new started subscriptions">
            <Line data={chartConfig(flagsLabels, ['Trial Plus', 'Paid Plus', 'Trial Songwriter', 'Paid Songwriter', 'Trial Pro', 'Paid Pro'],
              [trialSubscriptionPlus, paidSubscriptionPlus, trialSubscriptionSongwriter, paidSubscriptionSongwriter, trialSubscriptionPro, paidSubscriptionPro],
              [colors.variant_lightgreen, colors.calm_darkgreen, colors.variant_lightyellow, colors.calm_darkyellow, colors.variant_lightpurple, colors.calm_darkpurple], FILL, STACKED)} options={setOptions(STACKED)} />
          </ChartCard>
        </div>

        <div className="chart-col">
          <ChartCard header="First time login (stacked)" loading={flagsPerDay.loading} error={errors.flagsPerDay}
            values={[loginMyScorecloud, loginStudio, loginStudiorWin, loginStudioOsx, loginStudioOsxAppstore, loginSongwriter, loginSongwriterWin, loginSongwriterOsx, loginSongwriterOsxAppstore, loginExpress, loginExpressIpad, loginExpressIphone]}
            description="Number of users logging in to a client for the first time">
            <Line data={chartConfig(flagsLabels,
              ['My Scorecloud', 'Studio', 'Studio Win', 'Studio Osx', 'Studio Osx Appstore', 'Songwriter', 'Songwriter Win', 'Songwriter Osx', 'Songwriter Osx Appstore', 'Express', 'Express iPad', 'Express iPhone'],
              [loginMyScorecloud, loginStudio, loginStudiorWin, loginStudioOsx, loginStudioOsxAppstore, loginSongwriter, loginSongwriterWin, loginSongwriterOsx, loginSongwriterOsxAppstore, loginExpress, loginExpressIpad, loginExpressIphone],
              [colors.grey2, colors.green, colors.darkgreen, colors.teal, colors.darkteal, colors.yellow, colors.darkyellow, colors.orange, colors.darkorange, colors.lightblue, colors.blue, colors.darkblue], FILL, STACKED)} options={setOptions(STACKED)} />
          </ChartCard>
        </div>

        <div className="chart-col">
          <ChartCard header="Revenue from Successfully charged subscriptions" loading={analyticsEventsPerDay.loading} error={errors.analyticsEventsPerDay}
            values={[subscriptionChargedSuccessfullySum]} description="Sum of money paid to Doremir from ScoreCloud subscriptions (in USD from Braintree)">
            <Line data={chartConfig(analyticsEventsLabels, ['Earnings (USD)'], [subscriptionChargedSuccessfullySum], [colors.darkgreen])} options={setOptions()} />
          </ChartCard>
        </div>

        <div className="chart-col">
          <ChartCard header="Successfully charged subscriptions" loading={analyticsEventsPerDay.loading} error={errors.analyticsEventsPerDay}
            values={[subscriptionChargedSuccessfullySum, subscriptionChargedSuccessfully]} hideAvg hideTotal
            description="Number of charges compared to the amount of money charged.">
            <Line data={chartConfigLineMultiAxis(analyticsEventsLabels, ['Earnings (USD)', 'Number of charges'],
              [subscriptionChargedSuccessfullySum, subscriptionChargedSuccessfully], [colors.darkgreen, colors.secondary], undefined, undefined, ['y', 'y1'])}
              options={setOptions(undefined, undefined, undefined, MULTIAXIS)} />
          </ChartCard>
        </div>

        <div className="chart-col">
          <ChartCard header="Revenue from successfully charged subscriptions (Stacked)" loading={analyticsEventsPerDay.loading} error={errors.analyticsEventsPerDay}
            values={[subscriptionChargedSuccessfullyPlusSum, subscriptionChargedSuccessfullySongwriterSum, subscriptionChargedSuccessfullyProSum]}
            description="Sum per plan (in USD from Braintree)">
            <Line data={chartConfig(analyticsEventsLabels, ['Plus (USD)', 'Songwriter (USD)', 'Pro (USD)'],
              [subscriptionChargedSuccessfullyPlusSum, subscriptionChargedSuccessfullySongwriterSum, subscriptionChargedSuccessfullyProSum],
              [colors.calm_darkgreen, colors.calm_darkyellow, colors.calm_darkpurple], FILL, STACKED)} options={setOptions(STACKED)} />
          </ChartCard>
        </div>

        <div className="chart-col">
          <ChartCard header="Estimated Revenue From ScoreCloud Express Installs" loading={flagsPerDay.loading} error={errors.flagsPerDay}
            values={[expectedExpressRevenue, expectedExpressRevenueIpad, expectedExpressRevenueIphone]}
            description="First login in Express × $2.99. Unknowns are social logins.">
            <Line data={chartConfig(flagsLabels, ['Unknown (USD)', 'iPad (USD)', 'iPhone (USD)'],
              [expectedExpressRevenue, expectedExpressRevenueIpad, expectedExpressRevenueIphone],
              [colors.lightblue, colors.blue, colors.darkblue], FILL, STACKED)} options={setOptions(STACKED)} />
          </ChartCard>
        </div>

        <div className="chart-col">
          <ChartCard header="New Cloud Songs Per Client (Stacked)" loading={songsPerDay.loading} error={errors.songsPerDay}
            values={[newStudioSongs, newSongwriterSongs, newExpressSongs]}>
            <Line data={chartConfig(songsLabels, ['Studio', 'Songwriter', 'Express'],
              [newStudioSongs, newSongwriterSongs, newExpressSongs],
              [colors.darkteal, colors.darkyellow, colors.lightred], FILL, STACKED)} options={setOptions(STACKED)} />
          </ChartCard>
        </div>

        <div className="chart-col">
          <ChartCard header="New Cloud Songs Saved, Desktop vs Express (Stacked)" loading={songsPerDay.loading} error={errors.songsPerDay}
            values={[newStudioAndSongwriterSongs, newExpressSongs]}
            description="'Manual' = desktop, 'Recording' = Express.">
            <Line data={chartConfig(songsLabels, ['Desktop', 'Express'],
              [newStudioAndSongwriterSongs, newExpressSongs], [colors.teal, colors.lightred], FILL, STACKED)} options={setOptions(STACKED)} />
          </ChartCard>
        </div>

        <div className="chart-col">
          <ChartCard header="New Cloud Songs, appName" loading={songsPerDay.loading} error={errors.songsPerDay}
            values={[newStudioSongs, newSongwriterSongs]}>
            <Line data={chartConfig(songsLabels, ['Studio', 'Songwriter'],
              [newStudioSongs, newSongwriterSongs], [colors.darkteal, colors.darkyellow])} options={setOptions()} />
          </ChartCard>
        </div>

        <div className="chart-col">
          <ChartCard header="Songwriter app starts" loading={analyticsEventsPerDay.loading} error={errors.analyticsEventsPerDay}
            values={[programStartSongwriter]}>
            <Line data={chartConfig(analyticsEventsLabels, ['App starts'], [programStartSongwriter], [colors.primary])} options={setOptions()} />
          </ChartCard>
        </div>

        <div className="chart-col">
          <ChartCard header="Songwriter recording vs import" loading={analyticsEventsPerDay.loading} error={errors.analyticsEventsPerDay}
            values={[recordingStart, audioImport, videoImport]}>
            <Line data={chartConfig(analyticsEventsLabels, ['Recordings', 'Audio imports', 'YouTube imports'],
              [recordingStart, audioImport, videoImport], [colors.secondary, colors.primary, colors.teal])} options={setOptions()} />
          </ChartCard>
        </div>

        <div className="chart-col">
          <ChartCard header="Songwriter analysis started vs finished" loading={analyticsEventsPerDay.loading} error={errors.analyticsEventsPerDay}
            values={[songDisplay, recordingPlusImport]} hideAvg hideTotal>
            <Line data={chartConfig(analyticsEventsLabels, ['Finished', 'Started'],
              [songDisplay, recordingPlusImport], [colors.green, colors.yellow], FILL)} options={setOptions()} />
          </ChartCard>
        </div>

        <div className="chart-col">
          <ChartCard header="Songwriter YouTube imports started vs finished" loading={analyticsEventsPerDay.loading} error={errors.analyticsEventsPerDay}
            values={[songDisplayYouTube, videoImport]} hideAvg hideTotal>
            <Line data={chartConfig(analyticsEventsLabels, ['Finished', 'Started'],
              [songDisplayYouTube, videoImport], [colors.primary, colors.teal], FILL)} options={setOptions()} />
          </ChartCard>
        </div>

        <div className="chart-col">
          <ChartCard header="Web Downloads (Stacked)" loading={analyticsEventsPerDay.loading} error={errors.analyticsEventsPerDay}
            values={[webDownloadSongwriterMac, webDownloadSongwriterWin, webDownloadStudioMac, webDownloadStudioWin]}>
            <Line data={chartConfig(analyticsEventsLabels, ['Songwriter Mac', 'Songwriter Win', 'Studio Mac', 'Studio Win'],
              [webDownloadSongwriterMac, webDownloadSongwriterWin, webDownloadStudioMac, webDownloadStudioWin],
              [colors.yellow, colors.darkyellow, colors.teal, colors.darkteal], FILL, STACKED)} options={setOptions(STACKED)} />
          </ChartCard>
        </div>
      </div>
    </section>
  );
};

export default DashboardPage;
